import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Layers } from "lucide-react";
import { pillarGrain, pillarKey } from "@/lib/utils/daylight-insight/pillars";
import {
  DEFAULT_SPEC,
  filterAllLabel,
  filterLabel,
  primaryFilters,
  secondaryFilters,
  type FilterSpec,
} from "@/lib/utils/daylight-insight/dashboardSpec";
import { formatDateRange } from "@/lib/utils/daylight-insight/dates";
import { toDisplayLabel } from "@/lib/utils/daylight-insight/format";
import { useStore } from "@/provider/store";
import {
  DateRangePicker,
  FocusFilter,
  MultiSelect,
} from "@/components/daylight-insight/controls";
import {
  ApiFlowCard,
  ApiCompactMetric,
} from "@/components/daylight-insight/apiCards";
import {
  useDashboardIdForPillar,
  useDashboardMetricsForPillar,
} from "./useDashboardMetricsForPillar";
import { DashboardPerfsProvider, useDashboardPerfsContext } from "@/components/daylight-insight/api/DashboardPerfsProvider";
import { DashboardMetricsSkeleton } from "@/components/skeleton/DashboardMetricsSkeleton";
import {
  MetricTrendsPanel,
  type DrillDisciplineScope,
  type DrillFocus,
  type DrillScopeChip,
} from "./MetricTrendsPanel";
import { useMetricDisciplineNotifications } from "@/components/daylight-insight/api/useMetricDisciplineNotifications";
import type { DisciplineNotificationParams, MetricSummaryRow } from "@/components/daylight-insight/api/types";
import DisciplineBreakdownCardSkeleton from "./DisciplineBreakdownCardSkeleton";
// Type-only: erased at build time, so it does NOT pull the lazy card chunk.
import type { DisciplineCardMode } from "./DisciplineBreakdownCard";
import { DashboardTable, type DashboardTableContext } from "./DashboardTables";
import {
  parseBenchmarkLabel,
  toDisciplineSeries,
} from "@/components/daylight-insight/api/disciplineSeries";

/** The filter the by-Discipline card's drill narrows (`@Disciplines`) — that
 *  card plots one metric across disciplines, so it is tied to this filter. */
const DISCIPLINE_KEY = "discipline";

// Lazy so these chunks never block the dashboard's initial render.
const DisciplineBreakdownCard = lazy(
  () => import("./DisciplineBreakdownCard"),
);

/**
 * One dashboard tab.
 *
 * The tab's FIRST call is `dashboard/custom-views` — mounted here as the perfs
 * provider, above everything that requests data. It carries both of the user's
 * saved preferences for this dashboard: the filter selections, which the page
 * applies before it asks for any metrics, and the table column layouts, which
 * the By-Branch / By-Clinician tables read off the same response instead of
 * re-requesting it once they mount.
 */
export function PillarPage({ pillarK }: { pillarK: string }) {
  const dashboardID = useDashboardIdForPillar(pillarK);
  return (
    <DashboardPerfsProvider dashboardID={dashboardID}>
      <PillarPageBody pillarK={pillarK} />
    </DashboardPerfsProvider>
  );
}

function PillarPageBody({ pillarK }: { pillarK: string }) {
  const s = useStore();
  const section = [...s.allSections, s.allSnap].find(
    (sec) => pillarKey(sec.title) === pillarK,
  );
  // Heading: the server's own dashboard label, so the page is titled exactly as
  // its tab reads (both come from `dashboard/config` — see `availableTabs`) and
  // renaming a dashboard needs no frontend change. The seeded section title is
  // only the fallback for a pillar with no grant row to name it; dashboards need
  // no seeded section to render, hence the second fallback too.
  // The dashboard's RENDER SPEC — filter bar, detail table, drill views, extra
  // cards. Server-described where `config.dashboards` covers this dashboard,
  // this build's defaults where it doesn't (see `dashboardSpec.ts`). Nothing
  // below branches on WHICH dashboard is on screen: everything that used to be
  // a `pillarK === "…"` check reads a field of this object instead.
  const spec = s.specFor(pillarK);
  const title = spec?.label ?? section?.title ?? "—";
  // PRIMARY filters are the bar's dropdowns; NON-primary ones are the "+ Focus"
  // dimensions, shown as chips once picked. Both are the same kind of filter —
  // one selection state, sent the same way — only the control differs. Every
  // filter the page sends has a control showing it: a selection the bar can't
  // show (say, one restored from a saved view) must never narrow the numbers
  // invisibly.
  //
  // A filter is whatever the config names: its options are the config node its
  // `key` (and `source`) points at, and its selection goes to the metrics SP's
  // `@Filters` under that same key. A filter whose node the config doesn't send
  // has nothing to pick from, so it gets no control.
  const allFilterSpecs = spec?.filters ?? DEFAULT_SPEC.filters;
  const filterSpecs: FilterSpec[] = primaryFilters(allFilterSpecs).filter((f) => s.hasFilterOptions(f));
  const focusSpecs: FilterSpec[] = secondaryFilters(allFilterSpecs).filter((f) => s.hasFilterOptions(f));
  /** Every filter the page renders and sends: dropdowns, then Focus dimensions. */
  const activeSpecs: FilterSpec[] = [...filterSpecs, ...focusSpecs];
  // Detail tables, in the order the spec lists them.
  const tables = spec?.tables ?? DEFAULT_SPEC.tables;
  // The by-Discipline card, when this dashboard carries one. Its metric code is
  // the spec's, so the card plots whatever the dashboard says it plots.
  const disciplineSection =
    spec?.sections.find((sec) => sec.type === "disciplineBreakdown") ?? null;
  const disciplineMetricCode = disciplineSection?.metricCode ?? "";
  // Selected option LABELS per filter key — what the dropdowns show and select
  // by. Converted to option values at the API boundary.
  const [selected, setSelected] = useState<Record<string, string[]>>({});
  const selectedOf = (key: string) => selected[key] ?? [];
  const setFilter = (key: string) => (next: string[]) =>
    setSelected((x) => ({ ...x, [key]: next }));

  // Whether the current filter selection came from a drill Focus. Focus is an
  // exploration, not a preference: while it holds, the metrics SP is told not
  // to save the selection (`@SaveUserFilterPreferences = 0`). Any change the
  // user makes through a filter control hands the selection back to them.
  const [focusDriven, setFocusDriven] = useState(false);
  const byHand =
    <T,>(set: (v: T) => void) =>
    (v: T) => {
      setFocusDriven(false);
      set(v);
    };

  // Members a drill Focus added that the dropdown's own option list lacks (a
  // discipline the config list doesn't carry, a payor outside the lookup):
  // filter key → label → the value the API takes (the label itself for the
  // name-valued filters). They join that dropdown's options for the session so
  // the selection stays visible and can be unticked there like any other.
  const [focusExtras, setFocusExtras] = useState<Record<string, Record<string, string>>>({});
  const withExtras = (key: string, options: string[]) => {
    const extra = Object.keys(focusExtras[key] ?? {}).filter((l) => !options.includes(l));
    return extra.length ? [...options, ...extra] : options;
  };
  /** The labels a filter's dropdown lists: its config options plus any Focus
   *  extras. */
  const optionLabels = (f: FilterSpec) =>
    withExtras(f.key, s.filterOptionsFor(f).map((o) => o.label));
  /** A selected label → the value the API takes. */
  const valueOf = (f: FilterSpec, label: string) =>
    s.filterOptionsFor(f).find((o) => o.label === label)?.value ?? focusExtras[f.key]?.[label];

  // Switching service line swaps out every list scoped to it (branches), so a
  // selection the new list can't show is dropped rather than left filtering to
  // nothing. Lists that aren't scoped keep their selection.
  const lastLineRef = useRef(s.serviceLineSystemID);
  useEffect(() => {
    if (lastLineRef.current === s.serviceLineSystemID) return;
    lastLineRef.current = s.serviceLineSystemID;
    setSelected((x) => {
      const next: Record<string, string[]> = {};
      for (const f of activeSpecs) {
        const labels = optionLabels(f);
        const kept = (x[f.key] ?? []).filter((l) => labels.includes(l));
        if (kept.length) next[f.key] = kept;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.serviceLineSystemID]);

  // ── Saved filters (dashboard/custom-views, PerfCode = 'UserFilter') ────────
  // The dashboard opens on the filter selection the user last saved for it
  // rather than on the defaults. `filtersApplied` gates every data call below:
  // until the saved selection is in the controls, nothing may be requested —
  // otherwise the first request would go out with the default filters and be
  // thrown away by a second one a moment later.
  //
  // It is a one-shot. Once applied, later renders (and the refetch a view save
  // triggers) must not overwrite what the user has since picked by hand.
  const perfs = useDashboardPerfsContext();
  const [filtersApplied, setFiltersApplied] = useState(false);
  useEffect(() => {
    if (filtersApplied || !perfs.ready) return;
    const f = perfs.filters;
    if (f) {
      // The saved row holds option values per config filter key; the controls
      // work in labels. A value with no option behind it any more (a branch
      // this user lost access to, a payor that was removed) is dropped, and a
      // list that resolves to nothing leaves that filter on its default — never
      // on a selection the dropdown can't show.
      const restored: Record<string, string[]> = {};
      for (const spec of activeSpecs) {
        const saved = f.values[spec.key];
        if (!saved?.length) continue;
        const options = s.filterOptionsFor(spec);
        const labels = saved
          .map((v) => options.find((o) => o.value === v)?.label)
          .filter((l): l is string => !!l);
        if (labels.length) restored[spec.key] = labels;
      }
      if (Object.keys(restored).length) setSelected(restored);
      // Both ends or neither — half a range would silently pair a saved date
      // with today's default.
      if (f.startDate && f.endDate) s.setRange({ start: f.startDate, end: f.endDate });
    }
    setFiltersApplied(true);
    // The store object is rebuilt every render, so its lookups can't be
    // dependencies; the effect is guarded by `filtersApplied` instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersApplied, perfs.ready, perfs.filters]);

  // The filters are SERVER-SIDE: each selection goes to the metric API as its
  // options' values under the filter's own key (the UI works in labels,
  // converted here), so changing a filter re-fires the request — exactly like a
  // date-range change — rather than filtering the returned rows on the client.
  // Null when nothing is selected.
  const appliedEntries = activeSpecs
    .map((f): [string, string[]] => [
      f.key,
      selectedOf(f.key)
        .map((l) => valueOf(f, l))
        .filter((v): v is string => !!v),
    ])
    .filter(([, values]) => values.length > 0);
  const appliedFilters = appliedEntries.length ? Object.fromEntries(appliedEntries) : null;

  // Live metric values for this dashboard (KPI cards + more-metrics). The
  // dashboard id doubles as the By-Branch table's @DashboardID.
  const { category, loading, error, dashboardID } = useDashboardMetricsForPillar(
    pillarK,
    {
      filters: appliedFilters,
      saveUserFilterPreferences: !focusDriven,
    },
    // Held until the saved filters are in the controls — see above.
    filtersApplied,
  );

  // ── Load order, every dashboard ───────────────────────────────────────────
  // The dashboard-metrics call owns the first paint. Nothing else — no
  // secondary API, no lazy chunk — is allowed out until it has resolved, so the
  // KPIs never queue behind a slower table query.
  //   metricsPending → the call hasn't resolved yet (or can't fire yet because
  //                    the config/service line is still resolving, or because
  //                    the saved filters it must carry haven't been applied)
  //   metricsReady   → resolved, no error, metrics in hand: secondary calls may
  //                    now fire and their lazy chunks may mount
  // Anything else (error, or resolved with no metrics) means the secondary
  // sections have nothing to hang off, so they render nothing at all.
  const metricsPending =
    loading || !filtersApplied || dashboardID == null || !s.serviceLineSystemID;
  const metricsReady = !metricsPending && !error && !!category;

  // Patient Notifications only: once the dashboard-metrics call has landed, fire
  // the discipline-notifications API that feeds the by-Discipline card.
  //
  // The card's two modes want DIFFERENT bucketing, so each has its OWN call —
  // the flag is `@SummarizeByInterval`, which the SP reads as:
  //   1 → the interval the RANGE implies: one bucket per discipline, which is
  //       exactly the single value Snapshot's bars read.
  //   0 → the next interval down: the multi-bucket series Trend's panels plot.
  // Which mode is open therefore has to be known HERE, which is why the mode
  // state lives on the page rather than inside the card.
  const [disciplineMode, setDisciplineMode] = useState<DisciplineCardMode>("snapshot");
  const disciplineNotificationBase =
    disciplineSection && metricsReady && s.serviceLineSystemID
      ? {
          serviceLineSystemID: s.serviceLineSystemID,
          startDate: s.range.start,
          endDate: s.range.end,
          metricCodes: disciplineMetricCode,
          filters: appliedFilters,
        }
      : null;
  // Snapshot is the default mode, so this is the call that always fires — and
  // the one the load chain below hangs off.
  const disciplineSnapshotParams: DisciplineNotificationParams | null =
    disciplineNotificationBase
      ? { ...disciplineNotificationBase, summarizeByInterval: 1 }
      : null;
  // Trend is fetched LAZILY: nothing goes out until the user actually switches
  // to it, and the response is cached from then on (the params — the flag
  // included — ARE the query key), so toggling back and forth costs no further
  // requests.
  const disciplineTrendParams: DisciplineNotificationParams | null =
    disciplineNotificationBase && disciplineMode === "trend"
      ? { ...disciplineNotificationBase, summarizeByInterval: 0 }
      : null;
  const disciplineSnapshot = useMetricDisciplineNotifications(disciplineSnapshotParams);
  const disciplineTrend = useMetricDisciplineNotifications(disciplineTrendParams);
  // What the card renders: whichever mode is open.
  const disciplineActive =
    disciplineMode === "trend" ? disciplineTrend : disciplineSnapshot;

  // By-Clinician tables (`DashboardTables.tsx`) chain off the page's calls.
  //
  // Chaining differs by pillar: Patient Notifications waits for BOTH the metrics
  // call and the discipline-notifications call (`.data !== null`, even an empty
  // array, with no error/loading marks that second call as done), since its
  // graph sits above the table. A clinician-grain pillar with no graph in
  // between goes as soon as the metrics land.
  //
  // It is the SNAPSHOT call that gates it: the trend call may never be made at
  // all (it waits for the toggle), so chaining off that one would strand the
  // table on every visit that stays on the default view.
  const disciplineDone =
    metricsReady &&
    disciplineSnapshot.data !== null && !disciplineSnapshot.loading && !disciplineSnapshot.error;
  const clinicianReady = disciplineSection ? disciplineDone : metricsReady;
  // Upstream failure (metrics or discipline-summary) means the clinician call
  // never fires — hide the section rather than shimmer forever. Its OWN errors
  // still render inside the section.
  const clinicianUpstreamError = !!error || !!disciplineSnapshot.error;

  const apiMetrics = useMemo(() => category?.metrics ?? [], [category]);

  // Publish what this tab is showing so the header's Export button writes THIS
  // dashboard rather than a fixed workbook. Cleared on unmount so a tab with no
  // live metrics (Guide / All Metrics / Current State) falls back correctly.
  const { setActiveDashboard } = s;
  const pillarTitle = title;
  useEffect(() => {
    setActiveDashboard(apiMetrics.length ? { title: pillarTitle, metrics: apiMetrics } : null);
    return () => setActiveDashboard(null);
  }, [apiMetrics, pillarTitle, setActiveDashboard]);
  // KPI tiles = the server's key metrics (fall back to the first few); the rest
  // drop into "More metrics".
  const kpis = useMemo(() => {
    const key = apiMetrics.filter((m) => m.isKeyMetric);
    return key.length ? key : apiMetrics.slice(0, 4);
  }, [apiMetrics]);
  const moreMetrics = useMemo(() => {
    const kpiCodes = new Set(kpis.map((m) => m.code));
    return apiMetrics.filter((m) => !kpiCodes.has(m.code));
  }, [apiMetrics, kpis]);

  // Trends-View drill-down: a stack of metric codes (for related-metric
  // navigation) plus the active Chart/Table tab. Opened by clicking a card.
  const [drillStack, setDrillStack] = useState<string[]>([]);
  // The drill stays mounted while the page re-queries under a new filter (a
  // drill Focus does exactly that), reading the last category it had — so it
  // keeps its open view instead of closing and reopening on Trend.
  const lastCategoryRef = useRef(category);
  if (category) lastCategoryRef.current = category;
  const drillCategory = category ?? lastCategoryRef.current;
  const [drillTab, setDrillTab] = useState("chart");
  // A drill opened from the by-Discipline card is SCOPED to the discipline that
  // was clicked: every call the drill makes carries that one name, while the
  // page around it — filter bar, KPI cards, the card itself, the By-Clinician
  // table — keeps the dashboard's own selection. Null = no such scope (a drill
  // opened from a KPI / More-metrics card), which is why it is cleared on every
  // ordinary open and on close.
  const [drillDiscipline, setDrillDiscipline] = useState<string | null>(null);
  const openMetric = (m: { code: string }) => {
    setDrillStack([m.code]);
    setDrillTab("chart");
    setDrillDiscipline(null);
  };
  /** The by-Discipline card's open: the same drill, narrowed to one discipline. */
  const openMetricForDiscipline = (m: { code: string }, name: string) => {
    setDrillStack([m.code]);
    setDrillTab("chart");
    setDrillDiscipline(name);
  };
  const closeDrill = () => {
    setDrillStack([]);
    setDrillDiscipline(null);
  };
  // The metric the by-Discipline card plots. Its dashboard row is what a bar /
  // panel click drills into (the drill is keyed by metric code, so the card is
  // inert when this dashboard doesn't carry the metric) and — until the
  // discipline SP returns a target of its own — the only benchmark on offer:
  // the metric's own `benchmarkLabel`, used when it spells out a number.
  const disciplineMetric = useMemo(
    () =>
      disciplineMetricCode
        ? (apiMetrics.find(
            (m) => m.code.toLowerCase() === disciplineMetricCode.toLowerCase(),
          ) ?? null)
        : null,
    [apiMetrics, disciplineMetricCode],
  );
  // The figure the by-Discipline card states for each discipline — the Snapshot
  // payload, which is the one bucketed by the whole date range and therefore the
  // number a clicked row actually shows. Handed to the drill so its headline is
  // the row's own value instead of the dashboard's all-discipline total.
  const disciplineValues = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const row of toDisciplineSeries(disciplineSnapshot.data).series) {
      map.set(row.discipline, row.latest);
    }
    return map;
  }, [disciplineSnapshot.data]);
  const disciplineBenchmark = useMemo(
    () =>
      parseBenchmarkLabel(
        disciplineMetric?.benchmarkLabel,
        disciplineMetric?.higherIsBetter,
      ),
    [disciplineMetric],
  );
  // ── Focus (drill "By …" bars) ─────────────────────────────────────────────
  // Every filter with a drill tab is multi-select, so Focus on a bar ADDS that
  // member to the dropdown's selection (and × on a focused bar removes it) —
  // the same state the dropdown writes — so the page re-queries with the params
  // it already sends. A selection on "All" (nothing or everything ticked)
  // counts as no focus, so the first Focus narrows to just that member. The
  // dropdown flashes so the change is visible behind the slide-over. A summary
  // row is matched to its option by code/SystemID first, then by name. A row
  // that matches no option still offers Focus: its name joins the dropdown as an
  // extra option (`focusExtras`) — see `focusFor` for when it can't. A primary
  // filter's Focus sets its dropdown; a non-primary one's (a "More ▾" bar)
  // adds a "+ Focus" chip — same state either way.
  const [flash, setFlash] = useState<{ key: string; n: number } | null>(null);
  const flashFilter = (key: string) => setFlash((f) => ({ key, n: (f?.n ?? 0) + 1 }));
  const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
  const byName = (options: string[]) => (r: MetricSummaryRow) =>
    options.find((o) => sameName(o, r.name));
  /** Unmatched row → an extra option keyed by its name. `idOf` gives the id the
   *  API takes, or undefined when the row has none worth sending. */
  type Unmatched = (r: MetricSummaryRow) => string | undefined;
  const byOwnName: Unmatched = (r) => r.name;
  const byOwnId: Unmatched = (r) => (r.id && r.id !== r.name ? r.id : undefined);
  const multiFocus = (
    key: string,
    current: string[],
    optionCount: number,
    set: (next: string[]) => void,
    resolve: (r: MetricSummaryRow) => string | undefined,
    idOf?: Unmatched,
  ): DrillFocus => {
    const selected = current.length >= optionCount ? [] : current;
    // Unmatched rows seen this render, label → id, so `apply` can register the
    // label as an extra option of this filter.
    const unmatched = new Map<string, string>();
    return {
      selected,
      resolve: (r) => {
        const hit = resolve(r);
        if (hit || !idOf) return hit;
        const label = r.name.trim();
        const id = label ? idOf(r) : undefined;
        if (!id) return undefined;
        unmatched.set(label, id);
        return label;
      },
      apply: (label) => {
        const id = unmatched.get(label);
        if (id && focusExtras[key]?.[label] !== id) {
          setFocusExtras((x) => ({ ...x, [key]: { ...x[key], [label]: id } }));
        }
        set(selected.includes(label) ? selected : [...selected, label]);
        setFocusDriven(true);
        flashFilter(key);
      },
      remove: (label) => {
        set(selected.filter((x) => x !== label));
        setFocusDriven(true);
        flashFilter(key);
      },
    };
  };
  /**
   * A summary row is matched to the filter's option by id first — the option's
   * value or any of its own columns (a branch row's id is its BranchCode) —
   * then by name. An unmatched row joins the dropdown as an extra keyed by its
   * name, sending:
   *   • its name, when the filter is name-valued (value === label everywhere);
   *   • its id, when it carries one distinct from its name (payor, rep);
   *   • nothing — no Focus — when the list is scoped to the service line
   *     (branches): that list is the user's access scope.
   */
  const focusFor = (f: FilterSpec): DrillFocus | null => {
    // A drill opened from the by-Discipline card is already pinned to one
    // discipline, so its By Discipline bars can't be re-focused.
    if (f.key === DISCIPLINE_KEY && drillDiscipline) return null;
    const options = s.filterOptionsFor(f);
    const labels = optionLabels(f);
    const scoped = options.some((o) => !!o.ServiceLineSystemID);
    const nameValued = options.every((o) => o.value === o.label);
    return multiFocus(
      f.key,
      selectedOf(f.key),
      labels.length,
      setFilter(f.key),
      (r) =>
        options.find(
          (o) =>
            (!!r.id && (o.value === r.id || Object.values(o).includes(r.id))) ||
            sameName(o.label, r.name),
        )?.label ?? byName(labels)(r),
      scoped ? undefined : nameValued ? byOwnName : byOwnId,
    );
  };
  const drillFocus: Record<string, DrillFocus> = {};
  for (const f of activeSpecs) {
    const fx = focusFor(f);
    if (fx) drillFocus[f.key] = fx;
  }

  // The drill header restates this page's filter bar: same filters, same order,
  // same wording — so the modal says out loud which scope its numbers were
  // fetched under. A filter the user narrowed reads in the accent; one still on
  // its default ("All Payors") is shown muted rather than dropped, so the scope
  // is complete either way.
  const filterChip = (
    label: string,
    selected: string[],
    options: string[],
    allLabel: string,
    noun: string,
  ): DrillScopeChip => {
    // "All" means untouched OR everything ticked — the same rule the controls
    // themselves summarise by.
    const all = selected.length === 0 || selected.length === options.length;
    return {
      label,
      value: all
        ? allLabel
        : selected.length === 1
          ? toDisplayLabel(selected[0])
          : `${selected.length} ${noun}`,
      on: !all,
    };
  };
  const scopeChips: DrillScopeChip[] = [
    { value: formatDateRange(s.range), on: true },
    { value: s.service === "hh" ? "Home Health" : "Hospice" },
    ...filterSpecs.map((f): DrillScopeChip =>
      // Scoped drill → the chip names the clicked discipline (and offers a way
      // back to the page's own scope). Otherwise it restates the bar.
      f.key === DISCIPLINE_KEY && drillDiscipline
        ? {
            label: filterLabel(f),
            value: drillDiscipline,
            on: true,
            onClear: () => setDrillDiscipline(null),
          }
        : filterChip(filterLabel(f), selectedOf(f.key), optionLabels(f), filterAllLabel(f), "selected"),
    ),
    // Focus dimensions only when narrowed — like the bar, which shows a chip
    // for a picked member and nothing for an untouched dimension.
    ...focusSpecs
      .filter((f) => selectedOf(f.key).length > 0)
      .map((f) => filterChip(filterLabel(f), selectedOf(f.key), optionLabels(f), filterAllLabel(f), "selected")),
  ];

  // The relations API takes the same filter/date context as the metrics call.
  const relationBase = {
    serviceLineSystemID: s.serviceLineSystemID,
    startDate: s.range.start,
    endDate: s.range.end,
    filters: appliedFilters,
  };

  // The base the DRILL fetches under. Identical to the page's, except when the
  // drill was opened from the by-Discipline card: then `filters.discipline`
  // carries the clicked discipline alone, which narrows every call the drill makes —
  // the trend (metric-relations), all four summary views, and both Compare
  // modes — because they all spread this one object. Nothing on the dashboard
  // reads it, so the page's own numbers are untouched.
  //
  // The name is resolved back to the config option's value first: the graph
  // trims what the SP sends ("HHA " → "HHA") while the options carry it
  // verbatim, and `@Filters` should receive exactly what the filter bar
  // would have sent. No match (an option list that hasn't landed) → the trimmed
  // name, which is what the SP returned in the first place.
  const disciplineFilterValue = (name: string) => {
    const want = name.trim().toLowerCase();
    return (
      s
        .filterOptionsFor({ key: DISCIPLINE_KEY })
        .find((o) => o.label.trim().toLowerCase() === want)?.value ?? name.trim()
    );
  };
  const drillRelationBase = drillDiscipline
    ? {
        ...relationBase,
        filters: { ...appliedFilters, [DISCIPLINE_KEY]: [disciplineFilterValue(drillDiscipline)] },
      }
    : relationBase;

  // What the drill needs to state a one-discipline headline: the name (for the
  // fallback lookup) plus the card's own figure for it, when the card has one.
  // The seed is keyed by the metric it was measured for, so drilling on to a
  // related metric drops it and the drill fetches that metric's value instead.
  const drillDisciplineScope: DrillDisciplineScope | null = drillDiscipline
    ? {
        discipline: drillDiscipline,
        seed:
          disciplineMetric && disciplineValues.has(drillDiscipline)
            ? {
                code: disciplineMetric.code,
                value: disciplineValues.get(drillDiscipline) ?? null,
              }
            : undefined,
      }
    : null;

  // What every detail table reads off this page — see `DashboardTableContext`.
  const tableCtx: DashboardTableContext = {
    remountKey: pillarK + s.service,
    dashboardID,
    serviceLineSystemID: s.serviceLineSystemID,
    range: s.range,
    filters: appliedFilters,
    category,
    metricsPending,
    metricsReady,
    clinicianReady,
    clinicianUpstreamError,
  };

  const lyr = <Layers size={14} style={{ color: "var(--ink-3)" }} />;
  const renderFilter = (f: FilterSpec) => (
    <MultiSelect
      key={f.key}
      label={filterLabel(f)}
      icon={lyr}
      options={optionLabels(f)}
      selected={selectedOf(f.key)}
      onChange={byHand(setFilter(f.key))}
      allLabel={filterAllLabel(f)}
    />
  );

  return (
    <div className="pp">
      <div className="pp-head">
        <div>
          <div className="pp-title">{title}</div>
        </div>
        {/* The server's own grain wording when it sent one — the same string
            `[DayLight].[GetMetricDefinitions]` states for the dashboard — else
            the grain the seeded metric registry implies. */}
        <span className="pp-grainchip">
          {spec?.drillGrain
            ? spec.drillGrain.replace(/^drill\s+by\s+/i, "Drill by ")
            : `Drill by ${
                tables[0]?.kind === "clinician"
                  ? "Clinician · Discipline"
                  : pillarGrain(section ? section.ids : [])
              }`}
        </span>
      </div>
      <div className="pp-filters">
        <DateRangePicker range={s.range} onChange={s.setRange} maxIso={s.maxDateIso} />
        {filterSpecs.map((f) =>
          drillFocus[f.key] ? (
            // Wrapped so a drill Focus can flash the control it just set.
            <span key={f.key} className="pp-fwrap">
              {renderFilter(f)}
              {flash?.key === f.key && <span key={flash.n} className="pp-flash" />}
            </span>
          ) : (
            renderFilter(f)
          ),
        )}
        {focusSpecs.length > 0 && (
          <FocusFilter
            dims={focusSpecs.map((f) => ({
              key: f.key,
              label: filterLabel(f),
              options: optionLabels(f),
              selected: selectedOf(f.key),
            }))}
            onChange={(key, next) => byHand(setFilter(key))(next)}
          />
        )}
      </div>
      {metricsPending ? (
        // Skeleton for the metrics-driven header (KPIs + more-metrics). Uses
        // `metricsPending`, not `loading`, so the shimmer is up from the first
        // paint — including the window before the config resolves the dashboard
        // id, when the request hasn't even been fireable yet. The By-Branch
        // table has its own section and skeleton below, so it's not part of this
        // one.
        <DashboardMetricsSkeleton showTable={false} />
      ) : error && !apiMetrics.length ? (
        <div
          className="pp-card"
          style={{ padding: 22, textAlign: "center", color: "var(--ink-3)" }}
        >
          {error}
        </div>
      ) : (
        <>
          <div className="pp-kpis">
            {kpis.map((m) => (
              <ApiFlowCard key={m.code} metric={m} onSelect={openMetric} selected={drillStack[0] === m.code} />
            ))}
          </div>
          {moreMetrics.length > 0 && (
            <div className="pp-more">
              <div className="pp-more-lbl">More metrics</div>
              <div className="pp-more-grid">
                {moreMetrics.map((m) => (
                  <ApiCompactMetric
                    key={m.code}
                    metric={m}
                    onSelect={openMetric}
                    selected={drillStack[0] === m.code}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {/* Patient Notifications only — the by-Discipline card (Snapshot | Trend).
          While the metrics call is still in flight the plain skeleton stands in
          (holding the layout without pulling the chunk); the lazy card mounts
          only once the metrics have rendered. */}
      {disciplineSection &&
        (metricsPending ? (
          <DisciplineBreakdownCardSkeleton />
        ) : metricsReady ? (
          <Suspense fallback={<DisciplineBreakdownCardSkeleton />}>
            <DisciplineBreakdownCard
              data={disciplineActive.data}
              loading={disciplineActive.loading}
              error={disciplineActive.error}
              mode={disciplineMode}
              onModeChange={setDisciplineMode}
              // Trend's panels print THESE (the period figures) rather than the
              // last bucket of the line they draw — see `periodData`.
              periodData={disciplineSnapshot.data}
              periodLabel={formatDateRange(s.range)}
              benchmark={disciplineBenchmark}
              lowerBetter={disciplineMetric?.higherIsBetter === false}
              onOpenMetric={
                disciplineMetric
                  ? (d) => openMetricForDiscipline(disciplineMetric, d)
                  : undefined
              }
            />
          </Suspense>
        ) : null)}
      {/* Detail tables — one per entry in the spec's `tables`, in order. Each
          owns its fetch and its skeleton, chained behind the metrics call so
          the KPIs paint first (see `DashboardTables.tsx`). */}
      {tables.map((t) => (
        <DashboardTable key={t.key} table={t} ctx={tableCtx} />
      ))}
      {drillCategory && drillStack.length > 0 && (
        <MetricTrendsPanel
          category={drillCategory}
          stack={drillStack}
          setStack={setDrillStack}
          onClose={closeDrill}
          tab={drillTab}
          setTab={setDrillTab}
          relationBase={drillRelationBase}
          scopeChips={scopeChips}
          disciplineScope={drillDisciplineScope}
          focus={drillFocus}
          dashboardKey={pillarK}
          nowScope
        />
      )}
    </div>
  );
}
